import React, {useEffect, useState} from "react";
import PlotlyPlot from "../../../../common/components/PlotlyPlot";
import {loadProtocolBiomarkerData, loadProtocolBiomarkerOptions, loadProtocolBiomarkerNames} from "../../services/protocols";
import {PlotParams} from "react-plotly.js";
import {IProtocol} from "../../../types/protocol";
import {ICompany} from "../../../../common/types";
import {BiomarkerColumnNames} from "../../types/biomarkerNames";
import {ChartTypes} from "../charts";
import {Affix, Box, Loader, Text, Select} from "@mantine/core";

type PlotMode = "time" | "phase";

interface BiomarkerTimeseriesChartProps {
    activeCompany: ICompany;
    selectedProtocol: IProtocol | undefined;
    refreshTrigger?: number
}

export const BiomarkerTimeseriesChart: React.FC<BiomarkerTimeseriesChartProps> = ({activeCompany, selectedProtocol, refreshTrigger}) => {
    const [biomarkerTimeseriesData, setBiomarkerTimeseriesData] = useState<PlotParams>()
    const [loading, setLoading] = useState<boolean>(false)
    const [selectedBiomarker, setSelectedBiomarker] = useState<string>(null)
    const [frequencyBand, setFrequencyBand] = useState<string | null>(null)
    const [channelName, setChannelName] = useState<string | null>(null)
    const [freqOptions, setFreqOptions] = useState<{ value: string; label: string }[]>([]);
    const [channelOptions, setChannelOptions] = useState<{ value: string; label: string }[]>([]);
    const [biomarkerOptions, setBiomarkerOptions] = useState<{ value: string; label: string }[]>([]);
    const [parameterRefreshTick, setParameterRefreshTick] = useState<number>(0)
    const [plotMode, setPlotMode] = useState<PlotMode>("time")

    const column_names: BiomarkerColumnNames[] = [BiomarkerColumnNames.PRE_STIM_TIMES, BiomarkerColumnNames.PRE_STIM_VALUES, BiomarkerColumnNames.STIM_TIMES, BiomarkerColumnNames.STIM_VALUES, BiomarkerColumnNames.POST_STIM_TIMES, BiomarkerColumnNames.POST_STIM_VALUES]
    const require_all_columns: boolean = false

    useEffect(() => {
        async function initializeBiomarkerOptions() {
            if (selectedProtocol) {
                setLoading(true);
                try {
                    const biomarkerResponse = await loadProtocolBiomarkerNames(activeCompany.id, selectedProtocol, column_names, require_all_columns);
                    const biomarker_names = biomarkerResponse.data.map((biomarker) => ({ value: biomarker, label: biomarker }));
                    setBiomarkerOptions(biomarker_names);
                    if (biomarker_names.length > 0) {
                        setSelectedBiomarker(biomarker_names[0].value);
                    }
                } catch (error) {
                    console.error("Failed to load biomarker options", error);
                } finally {
                    setLoading(false);
                }
            }
        }
        initializeBiomarkerOptions();
    }, [selectedProtocol, refreshTrigger]);

    useEffect(() => {
        async function updateDropdownOptions() {
            if (selectedProtocol && selectedBiomarker) {
                // load frequency band options
                const frequencyBands = await loadProtocolBiomarkerOptions(activeCompany.id, selectedProtocol, selectedBiomarker, BiomarkerColumnNames.FREQUENCY_BAND);

                // load channel name options
                const channelNames = await loadProtocolBiomarkerOptions(activeCompany.id, selectedProtocol, selectedBiomarker, BiomarkerColumnNames.CHANNEL_NAME);

                // Convert string lists to mappings for dropdown options
                // Filter out null or undefined or empty string values to avoid invalid dropdown options
                const isValidValue = (val: any): boolean => {
                    return val !== null &&
                           val !== undefined &&
                           val !== "" &&
                           typeof val === 'string';
                };
                const freqOptionsMapped = (frequencyBands?.data && Array.isArray(frequencyBands.data)) ? frequencyBands.data.filter(isValidValue).map((band) => ({ value: band, label: band })) : [];
                const channelOptionsMapped = (channelNames?.data && Array.isArray(channelNames.data)) ? channelNames.data.filter(isValidValue).map((channel) => ({ value: channel, label: channel })) : [];

                setFreqOptions(freqOptionsMapped);
                setChannelOptions(channelOptionsMapped);
                if (freqOptionsMapped.length > 0) {
                        setFrequencyBand(freqOptionsMapped[0].value);
                    } else {
                        setFrequencyBand(null); // Reset to null if no options available
                    }
                if (channelOptionsMapped.length > 0) {
                        setChannelName(channelOptionsMapped[0].value);
                    } else {
                        setChannelName(null); // Reset to null if no options available
                    }
                setParameterRefreshTick((t) => t + 1)
            }
        }
        updateDropdownOptions();
    }, [selectedBiomarker]);

    useEffect(() => {
        if (selectedProtocol && selectedBiomarker) {
            setLoading(true)
            loadProtocolBiomarkerData(activeCompany.id, selectedProtocol, ChartTypes.BIOMARKER_TIMESERIES, selectedBiomarker, column_names, frequencyBand, channelName).then((data: any) => {
                // data.data expected to be array of [stim_condition, pre_stim_times, pre_stim_values, stim_times, stim_values, post_stim_times, post_stim_values]

                type StimData = {
                    preTimes: string[];
                    preValues: number[];
                    stimTimes: string[];
                    stimValues: number[];
                    postTimes: string[];
                    postValues: number[];
                };
                const stimDataMap = new Map<string, StimData>();
                const stimConditions: string[] = [];

                (data.data || []).forEach(([stim, pre_times, pre_values, stim_times, stim_values, post_times, post_values]: [string, Array<string>, Array<number>, Array<string>, Array<number>, Array<string>, Array<number>]) => {
                    if (!stimDataMap.has(stim)) {
                        stimDataMap.set(stim, { preTimes: [], preValues: [], stimTimes: [], stimValues: [], postTimes: [], postValues: [] });
                        stimConditions.push(stim);
                    }
                    const stimData = stimDataMap.get(stim)!;
                    stimData.preTimes.push(...(pre_times || []));
                    stimData.preValues.push(...(pre_values || []));
                    stimData.stimTimes.push(...(stim_times || []));
                    stimData.stimValues.push(...(stim_values || []));
                    stimData.postTimes.push(...(post_times || []));
                    stimData.postValues.push(...(post_values || []));
                });

                // Phase region colours (matching the Python backend)
                const PHASE_COLORS = {
                    pre:  'rgba(255,215,0,0.25)',      // gold
                    stim: 'rgba(144,238,144,0.35)',    // lightgreen
                    post: 'rgba(255,160,122,0.30)',    // lightsalmon
                };

                const phaseRect = (x0: number | string, x1: number | string, color: string) => ({
                    type: 'rect' as const,
                    xref: 'x' as const,
                    yref: 'paper' as const,
                    x0, x1, y0: 0, y1: 1,
                    fillcolor: color,
                    line: { width: 0 },
                    layer: 'below' as const,
                });

                const traces: any[] = [];
                const shapes: any[] = [];
                const annotations: any[] = [];

                // Centred region label (no arrow) – mirrors Python _region_label
                const regionLabel = (xMid: number | string, text: string) => ({
                    x: xMid,
                    y: 1.0,
                    xref: 'x' as const,
                    yref: 'paper' as const,
                    yanchor: 'bottom',
                    text,
                    showarrow: false,
                    bgcolor: 'rgba(255,255,255,0.8)',
                    bordercolor: 'black',
                    borderwidth: 1,
                    font: { size: 10 },
                    align: 'center' as const,
                });

                // Midpoint of two ISO timestamp strings → ISO string
                const isoMid = (t0: string, t1: string): string =>
                    new Date((new Date(t0).getTime() + new Date(t1).getTime()) / 2).toISOString();

                // Helper: sort parallel time/value arrays, return typed pairs.
                // Only timestamps are filtered for validity; null/NaN values are kept so that
                // Plotly renders them as line gaps (connectgaps: false) and so that phase
                // rectangles are still drawn even when all values for a region are null/NaN.
                const sortedStrPairs = (times: string[], values: (number | null | undefined)[]) =>
                    times
                        .map((t, i) => ({ t, v: values[i] ?? null }))
                        .filter(p => p.t != null && p.t !== "")
                        .sort((a, b) => (a.t < b.t ? -1 : 1));

                const sortedMsPairs = (times: string[], values: (number | null | undefined)[]) =>
                    times
                        .map((t, i) => ({ ms: new Date(t).getTime(), v: values[i] ?? null }))
                        .filter(p => !isNaN(p.ms))
                        .sort((a, b) => a.ms - b.ms);

                // Extend a string-timestamp phase end by one sample period
                const extendStrTime = (pairs: { t: string }[]): string => {
                    if (pairs.length === 0) return '';
                    const lastMs = new Date(pairs[pairs.length - 1].t).getTime();
                    const samplePeriod = pairs.length >= 2
                        ? lastMs - new Date(pairs[pairs.length - 2].t).getTime()
                        : 0;
                    return new Date(lastMs + samplePeriod).toISOString();
                };

                // Duration of a ms-pairs phase extended by one sample period (seconds)
                const extendedDurSec = (pairs: { ms: number }[]): number => {
                    if (pairs.length === 0) return 0;
                    const firstMs = pairs[0].ms;
                    const lastMs  = pairs[pairs.length - 1].ms;
                    const samplePeriod = pairs.length >= 2
                        ? lastMs - pairs[pairs.length - 2].ms
                        : 0;
                    return (lastMs - firstMs + samplePeriod) / 1000;
                };

                if (plotMode === "time") {
                    // One trace, line broken between phases using null sentinels
                    const xVals: (string | null)[] = [];
                    const yVals: (number | null)[] = [];

                    stimConditions.forEach((stim) => {
                        const d = stimDataMap.get(stim)!;
                        const prePairs  = sortedStrPairs(d.preTimes,  d.preValues);
                        const stimPairs = sortedStrPairs(d.stimTimes,  d.stimValues);
                        const postPairs = sortedStrPairs(d.postTimes,  d.postValues);

                        // Append each phase with a null separator before it (except the very first point)
                        const appendPhase = (pairs: { t: string; v: number }[]) => {
                            if (pairs.length === 0) return;
                            if (xVals.length > 0) { xVals.push(null); yVals.push(null); }
                            pairs.forEach(p => { xVals.push(p.t); yVals.push(p.v); });
                        };
                        appendPhase(prePairs);
                        appendPhase(stimPairs);
                        appendPhase(postPairs);

                        // Phase shading shapes – pairs retain all timestamps regardless of value
                        // nullness, so rectangles are drawn even when all values are null/NaN.
                        if (prePairs.length >= 1)
                            shapes.push(phaseRect(prePairs[0].t, extendStrTime(prePairs), PHASE_COLORS.pre));
                        if (stimPairs.length >= 1)
                            shapes.push(phaseRect(stimPairs[0].t, extendStrTime(stimPairs), PHASE_COLORS.stim));
                        if (postPairs.length >= 1)
                            shapes.push(phaseRect(postPairs[0].t, extendStrTime(postPairs), PHASE_COLORS.post));

                        // Region labels
                        if (prePairs.length >= 1)
                            annotations.push(regionLabel(isoMid(prePairs[0].t, extendStrTime(prePairs)), 'Pre-stim'));
                        if (stimPairs.length >= 1)
                            annotations.push(regionLabel(isoMid(stimPairs[0].t, extendStrTime(stimPairs)), `Stim: ${stim}`));
                        if (postPairs.length >= 1)
                            annotations.push(regionLabel(isoMid(postPairs[0].t, extendStrTime(postPairs)), 'Post-stim'));
                    });

                    traces.push({
                        type: 'scatter',
                        mode: 'lines',
                        name: 'Biomarker',
                        x: xVals,
                        y: yVals,
                        connectgaps: false,
                        line: { color: '#636efa', width: 2 },
                    });
                } else {
                    // Per-stim-condition, aligned by phase: pre → stim → post stitched end-to-end
                    // First colour matches the time-plot trace; rest follow Plotly's vivid palette
                    // style while avoiding gold (pre), lightgreen (stim) and lightsalmon (post)
                    const conditionColors = [
                        '#636efa', // blue      – matches time plot
                        '#ef553b', // red
                        '#ab63fa', // purple
                        '#19d3f3', // cyan
                        '#ff6692', // pink
                        '#ff97ff', // magenta
                        '#00b4d8', // sky blue  – replaces clashing green (#00cc96)
                        '#f72585', // hot pink  – replaces clashing pale green (#b6e880)
                        '#4361ee', // indigo    – replaces clashing yellow (#fecb52)
                        '#e763fa', // violet    – replaces clashing salmon (#ffa15a)
                    ];

                    // Pass 1: compute maximum phase durations (used for shared rectangle boundaries)
                    let maxPreDur = 0, maxStimDur = 0, maxPostDur = 0;
                    const phasePairsCache: Array<{
                        prePairs: { ms: number; v: number | null }[];
                        stimPairs: { ms: number; v: number | null }[];
                        postPairs: { ms: number; v: number | null }[];
                    }> = [];

                    stimConditions.forEach((stim) => {
                        const d = stimDataMap.get(stim)!;
                        const prePairs  = sortedMsPairs(d.preTimes,  d.preValues);
                        const stimPairs = sortedMsPairs(d.stimTimes,  d.stimValues);
                        const postPairs = sortedMsPairs(d.postTimes,  d.postValues);
                        phasePairsCache.push({ prePairs, stimPairs, postPairs });

                        // Pairs retain all valid timestamps regardless of value nullness,
                        // so durations (and thus rectangle widths) are correct even when
                        // all values for a phase are null/NaN.
                        maxPreDur  = Math.max(maxPreDur,  extendedDurSec(prePairs));
                        maxStimDur = Math.max(maxStimDur, extendedDurSec(stimPairs));
                        maxPostDur = Math.max(maxPostDur, extendedDurSec(postPairs));
                    });

                    // Pass 2: build traces, pinning stim to maxPreDur and post to maxPreDur+maxStimDur
                    stimConditions.forEach((stim, idx) => {
                        const { prePairs, stimPairs, postPairs } = phasePairsCache[idx];

                        const toRelSec = (pairs: { ms: number; v: number | null }[], offsetSec: number, originMs: number) =>
                            pairs.map(p => ({ x: (p.ms - originMs) / 1000 + offsetSec, y: p.v }));

                        const preOrigin  = prePairs.length  > 0 ? prePairs[0].ms  : 0;
                        const stimOrigin = stimPairs.length > 0 ? stimPairs[0].ms : 0;
                        const postOrigin = postPairs.length > 0 ? postPairs[0].ms : 0;

                        // Always align to the shared rectangle edges
                        const relPre  = toRelSec(prePairs,  0,                       preOrigin);
                        const relStim = toRelSec(stimPairs, maxPreDur,               stimOrigin);
                        const relPost = toRelSec(postPairs, maxPreDur + maxStimDur,  postOrigin);

                        // Build x/y with null sentinels between phases to break line connections
                        const xRel: (number | null)[] = [];
                        const yRel: (number | null)[] = [];
                        const appendRelPhase = (phase: { x: number; y: number }[]) => {
                            if (phase.length === 0) return;
                            if (xRel.length > 0) { xRel.push(null); yRel.push(null); }
                            phase.forEach(p => { xRel.push(p.x); yRel.push(p.y); });
                        };
                        appendRelPhase(relPre);
                        appendRelPhase(relStim);
                        appendRelPhase(relPost);

                        if (xRel.length === 0) return;

                        const color = conditionColors[idx % conditionColors.length];
                        traces.push({
                            type: 'scatter',
                            mode: 'lines',
                            name: stim,
                            x: xRel,
                            y: yRel,
                            connectgaps: false,
                            line: { color, width: 2 },
                        });
                    });

                    // Add phase shading using maximum durations
                    if (maxPreDur  > 0) shapes.push(phaseRect(0, maxPreDur, PHASE_COLORS.pre));
                    if (maxStimDur > 0) shapes.push(phaseRect(maxPreDur, maxPreDur + maxStimDur, PHASE_COLORS.stim));
                    if (maxPostDur > 0) shapes.push(phaseRect(maxPreDur + maxStimDur, maxPreDur + maxStimDur + maxPostDur, PHASE_COLORS.post));

                    // Centred region labels for phase mode
                    if (maxPreDur  > 0) annotations.push(regionLabel(maxPreDur / 2, 'Pre-stim'));
                    if (maxStimDur > 0) annotations.push(regionLabel(maxPreDur + maxStimDur / 2, 'Stim'));
                    if (maxPostDur > 0) annotations.push(regionLabel(maxPreDur + maxStimDur + maxPostDur / 2, 'Post-stim'));
                }

                const xaxisOverride = plotMode === "time"
                    ? { type: 'date' as const, title: { text: 'Time' }, zeroline: false }
                    : { type: 'linear' as const, title: { text: 'Time (s, aligned by phase)' }, zeroline: false };

                setBiomarkerTimeseriesData({
                    layout: {
                        ...data.figure.layout,
                        xaxis: {
                            ...data.figure.layout.xaxis,
                            ...xaxisOverride,
                        },
                        yaxis: {
                            ...data.figure.layout.yaxis,
                        },
                        shapes,
                        annotations,
                        showlegend: plotMode === 'phase',
                    },
                    config: { responsive: true },
                    data: traces
                })
            }).catch(() => {
                // eat the error since notification would already be called
            }).finally(() => {
                setLoading(false)
            })
        }
    }, [selectedProtocol, parameterRefreshTick, plotMode])

    const renderLoading = () => {
        return (
            <Box style={{position: "absolute", zIndex: "10", top: "calc(50% - 2em)", left: "calc(50% - 2em)"}}>
                <Loader size="4em" color="var(--bios-strong-accent)"/>
            </Box>
        )
    }

    return (
        <Box style={{height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
            <Box style={{flex: '0 0 auto', display: 'flex', gap: '1rem', padding: '0.5rem', alignItems: 'center', overflow: 'hidden'}}>
                <Box style={{display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: '1 1 0', minWidth: 120, maxWidth: 180, overflow: 'hidden'}}>
                    <Text size="xs" color="dimmed">Biomarker</Text>
                    <Select
                        data={biomarkerOptions}
                        value={selectedBiomarker}
                        onChange={(v) => setSelectedBiomarker(v || null)}
                        style={{width: '100%'}}
                        disabled={biomarkerOptions.length === 0}
                    />
                </Box>
                <Box style={{display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: '1 1 0', minWidth: 120, maxWidth: 180, overflow: 'hidden'}}>
                    <Text size="xs" color="dimmed">Frequency band</Text>
                    <Select
                        data={freqOptions}
                        value={frequencyBand}
                        onChange={(v) => {
                            setFrequencyBand(v || null)
                            setParameterRefreshTick((t) => t + 1)
                            }}
                        style={{width: '100%'}}
                        disabled={freqOptions.length === 0}
                    />
                </Box>
                <Box style={{display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: '1 1 0', minWidth: 120, maxWidth: 180, overflow: 'hidden'}}>
                    <Text size="xs" color="dimmed">Channel Name</Text>
                    <Select
                        data={channelOptions}
                        value={channelName}
                        onChange={(v) => {
                            setChannelName(v || null)
                            setParameterRefreshTick((t) => t + 1)
                            }}
                        style={{width: '100%'}}
                        disabled={channelOptions.length === 0}
                    />
                </Box>
                <Box style={{display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: '1 1 0', minWidth: 120, maxWidth: 180, overflow: 'hidden'}}>
                    <Text size="xs" color="dimmed">Plot Mode</Text>
                    <Select
                        data={[
                            { value: 'time', label: 'Time' },
                            { value: 'phase', label: 'Phase Aligned' },
                        ]}
                        value={plotMode}
                        onChange={(v) => setPlotMode((v as PlotMode) || "combined")}
                        style={{width: '100%'}}
                    />
                </Box>
            </Box>

            <Box style={{flex: '1 1 auto', minHeight: 0, position: 'relative', overflow: 'hidden'}}>
                {biomarkerTimeseriesData ? (
                    <Box style={{height: '100%', width: '100%'}}>
                        <PlotlyPlot {...biomarkerTimeseriesData}/>
                        {loading ? renderLoading() : null}
                    </Box>
                ) : (
                    <Box style={{height: '100%', position: 'relative'}}>
                        {renderLoading()}
                    </Box>
                )}
            </Box>
        </Box>
    )
}
